'use client';

import {
	useJupiterTokenPairData,
	useTokenPairData,
} from 'src/hooks/swap/useTokenPairData';
import useSwapMarkets from 'src/hooks/swap/useSwapMarkets';
import SkeletonValuePlaceholder from '../SkeletonValuePlaceholder/SkeletonValuePlaceholder';
import { STABLE_COIN_SYMBOLS } from 'src/constants/constants';
import { TokenPairLogos } from '../Utils/MarketIcon';
import { useSpotMarkets } from 'src/hooks/spotMarkets/useSpotMarkets';
import { MARKET_STATS_BAR_CELL_CLASSNAME } from 'src/components/MarketStatsBar/consts';
import StatDisplay from 'src/components/MarketStatsBar/StatDisplay';
import { cm } from 'src/utils/classNames';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import ScrollShadows from 'src/components/ScrollShadows';
import { useRef } from 'react';
import TitanBadge from './TitanBadge';
import { getMarketPriceDecimals } from 'src/constants/marketDecimalConfig';

const SWAP_BAR_CELL_LABEL_CLASSNAME =
	'sm:mb-1 typo-b5 sm:typo-b4 text-text-secondary';

const SwapBar = () => {
	const { swapFromMarket, swapToMarket } = useSwapMarkets();
	const spotMarkets = useSpotMarkets();
	const isMobileScreenSize = useIsMobileScreenSize();
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	let fromMarket = Object.values(spotMarkets).find((spotMarket) =>
		swapFromMarket.mint.equals(spotMarket.mint)
	);
	let toMarket = Object.values(spotMarkets).find((spotMarket) =>
		swapToMarket.mint.equals(spotMarket.mint)
	);

	// always denominate by USDC if it's in the pair
	if (STABLE_COIN_SYMBOLS.includes(swapFromMarket.symbol)) {
		const temp = fromMarket;
		fromMarket = toMarket;
		toMarket = temp;
	}

	const { low24h, high24h, isLoading } = useTokenPairData(
		fromMarket?.symbol ?? '',
		toMarket?.symbol ?? ''
	);

	const tokenPairPrice = useJupiterTokenPairData(swapFromMarket, swapToMarket);

	const displayPrecision = getMarketPriceDecimals(fromMarket?.symbol ?? '');

	const fromTokenSymbol = fromMarket?.symbol;
	const toTokenSymbol = toMarket?.symbol;

	return (
		<ScrollShadows
			scrollableRef={scrollContainerRef}
			gradientFromClassName="from-main-bg"
			className="h-full"
		>
			<div
				className="hidden-scroll flex h-full items-center justify-between gap-x-xl overflow-x-auto overflow-y-hidden border-0 bg-container-bg pr-4 text-text-default sm:rounded-sm"
				ref={scrollContainerRef}
			>
				<div className="sm:gap-x-6xl flex min-w-max items-center gap-x-4xl px-4 py-3">
					<div className="flex shrink-0 items-center gap-x-lg sm:gap-x-4xl">
						<div className="flex shrink-0 items-center gap-x-sm p-sm">
							{isLoading || !fromMarket || !toMarket ? (
								<SkeletonValuePlaceholder loading className="h-6 w-40" />
							) : (
								<>
									<TokenPairLogos
										fromTokenSymbol={fromTokenSymbol ?? ''}
										toTokenSymbol={toTokenSymbol ?? ''}
										size={isMobileScreenSize ? 20 : 24}
									/>
									<span className="typo-t1">{`${fromTokenSymbol}/${toTokenSymbol}`}</span>
								</>
							)}
						</div>
						<div className="flex items-center gap-x-sm">
							{isLoading ? (
								<SkeletonValuePlaceholder loading className="h-6 w-28" />
							) : (
								<span className="typo-t1 text-text-default">
									{isNaN(tokenPairPrice)
										? '-'
										: tokenPairPrice.toFixed(displayPrecision)}
								</span>
							)}
						</div>
					</div>
					<div className="flex items-center">
						<div className={MARKET_STATS_BAR_CELL_CLASSNAME}>
							{isLoading ? (
								<SkeletonValuePlaceholder loading className="h-4 w-16" />
							) : (
								<StatDisplay
									header="24hr High"
									headerClassName={cm(SWAP_BAR_CELL_LABEL_CLASSNAME, 'mb-0')}
									className="whitespace-nowrap"
								>
									{isNaN(high24h) ? '-' : high24h.toFixed(displayPrecision)}
								</StatDisplay>
							)}
						</div>
						<div className={MARKET_STATS_BAR_CELL_CLASSNAME}>
							<StatDisplay
								header="24hr Low"
								headerClassName={cm(SWAP_BAR_CELL_LABEL_CLASSNAME, 'mb-0')}
								className="whitespace-nowrap"
							>
								{isNaN(low24h) ? '-' : low24h.toFixed(displayPrecision)}
							</StatDisplay>
						</div>
					</div>
				</div>
				{/* Titan badge display */}
				<TitanBadge className="shrink-0" />
			</div>
		</ScrollShadows>
	);
};

export default SwapBar;
